import React from "react";

import queryString from "query-string";

import { colorsPalettes } from "@similarweb/styles";

import { apiHelper } from "app/common/services/apiHelper";
import categoryService from "app/common/services/categoryService";
import { chosenSites } from "app/common/services/chosenSites";
import { swSettings } from "app/common/services/swSettings";
import { QualificationSideBarHOC } from "app/components/QualificationSideBar/QualificationSideBarHOC";
import { TableNoData } from "app/components/React/Table/FlexTable/Big/FlexTableStatelessComponents";
import { SWReactTableWrapperBox } from "app/components/React/Table/SWReactTableGhostLoader";
import SWReactTableWrapper from "app/components/React/Table/SWReactTableWrapper";
import { i18nFilter, translate } from "app/filters/i18nFilter";

import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { CHART_COLORS } from "../../../../../constants/ChartColors";
import { i18nCategoryFilter, smallNumbersPercentageFilter } from "../../../../../filters/ngFilters";
import type { IBackendData } from "../../../incoming-traffic/single/IncomingTrafficSingle";

import { IncomingTrafficDesktopTableTop } from "./IncomingTrafficDesktopTableTop";
import {
    IncomingTrafficCompareTableSettings,
    IncomingTrafficSingleTableSettings,
    IncomingTrafficSingleDesktopTableSettings,
    IncomingTrafficCompareDesktopTableSettings,
} from "./IncomingTrafficSingleTableSettings";
import { IncomingTrafficSingleTableTop } from "./IncomingTrafficSingleTableTop";

import BoxSubtitle from "pro-features/components/BoxSubtitle/src/BoxSubtitle";
import { PrimaryBoxTitle } from "pro-features/styled components/StyledBoxTitle/src/StyledBoxTitle";
import { SubTitleReferrals } from "app/pages/website-analysis/incoming-traffic/StyledComponents";
import { Separator, Title } from "app/pages/website-analysis/traffic-sources/mmx/single/StyledComponents";

import { buildFiltersForTable } from "./IncomingTrafficTableUtils";

import { proNavigator } from "single-spa/react-app/ProNavigator";

declare const similarweb;

interface IDesktopBackendData {
    Data;
    Filters: {
        category: Array<{
            id: string;
            text: string;
            formattedText: string;
            icon: string;
            count: number;
            children: Array<{
                id: string;
                text: string;
                formattedText: string;
                count: number;
            }>;
        }>;
        sourceType: Array<{
            id: string;
            text: string;
            count: number;
        }>;
    };
}
class IncomingTrafficRefferalTableInner extends React.PureComponent<any, any> {
    private swNavigator = proNavigator;
    private swSettings: any;
    private chosenSites = chosenSites;

    private isStructuredSource = (webSource: string) => webSource === "Desktop" || webSource === "Total";

    constructor(props) {
        super(props);
        this.swSettings = swSettings;
        this.state = {
            allCategories: [],
            allSourceTypes: [],
            sortedColumn: undefined,
        };
    }

    getTitleSection = () => {
        const title = i18nFilter()("analysis.common.trafficsource.referrals.websites");
        const { webSource } = this.swNavigator.getParams();
        const subTitleFilters = [
            {
                filter: "webSource",
                value: webSource,
            },
        ];
        return (
            <SWReactTableWrapperBox>
                <Title>
                    <PrimaryBoxTitle>{title}</PrimaryBoxTitle>
                    <SubTitleReferrals>
                        <BoxSubtitle filters={subTitleFilters} />
                    </SubTitleReferrals>
                </Title>
                <Separator />
                <TableNoData
                    messageTitle={i18nFilter()("analysis.source.referrals.table.no_data_mobile_subdomains")}
                    messageSubtitle={null}
                />
            </SWReactTableWrapperBox>
        );
    };

    public render() {
        const downloadExcelPermitted = this.swSettings.current.resources.IsExcelAllowed;
        const { search, webSource, isWWW, tableCategory, sourceType } = this.swNavigator.getParams();
        const selectedCategory = tableCategory ? categoryService.getCategory(tableCategory, "id") : null;

        return webSource === devicesTypes.MOBILE && isWWW !== "*" ? (
            this.getTitleSection()
        ) : (
            <SWReactTableWrapper
                serverApi={this.getApiEndpoint()}
                initialFilters={this.getInitialFilters()}
                dataParamsAdapter={this.dataParamsAdapter}
                tableColumns={this.getColumns()}
                transformData={this.isStructuredSource(webSource) ? this.transformStructuredData : this.transformData}
                getDataCallback={this.isStructuredSource(webSource) ? this.onGetStructuredData : this.onGetData}
                tableOptions={{
                    metric: "MarketingWorkspaceWebsiteGroupTable",
                    trackName: "Traffic Sources",
                }}
                recordsField="records"
                totalRecordsField="TotalCount"
                onSort={this.onSort}
                pageIndent={1}
            >
                {(topComponentProps) => {
                    return this.isStructuredSource(webSource) ? (
                        <IncomingTrafficDesktopTableTop
                            {...topComponentProps}
                            searchTerm={search}
                            selectedCategory={tableCategory !== "no-category" ? tableCategory : ""}
                            allCategories={this.state.allCategories}
                            selectedSourceType={sourceType}
                            allSourceTypes={this.state.allSourceTypes}
                            excelLink={this.getExcel()}
                            downloadExcelPermitted={downloadExcelPermitted}
                            onDateClick={this.props.onDateClick}
                            onCountryClick={this.props.onCountryClick}
                            onDateHover={this.props.onDateHover}
                            onCountryHover={this.props.onCountryHover}
                            onWebSourceHover={this.props.onWebSourceHover}
                        />
                    ) : (
                        <IncomingTrafficSingleTableTop
                            {...topComponentProps}
                            searchTerm={search}
                            selectedCategoryId={tableCategory}
                            categoryParamName={"tableCategory"}
                            selectedCategory={selectedCategory}
                            allCategories={this.state.allCategories}
                            excelLink={this.getExcel()}
                            downloadExcelPermitted={downloadExcelPermitted}
                            topics={this.state.topics}
                            categoriesData={this.state.categoriesData}
                            domainMetaData={this.state.domainMetaData}
                        />
                    );
                }}
            </SWReactTableWrapper>
        );
    }

    public getColumns = () => {
        const { duration, key, webSource } = this.swNavigator.getParams();
        const mode = key.split(",").length > 1 ? "compare" : "single";
        if (this.isStructuredSource(webSource)) {
            return mode === "single"
                ? IncomingTrafficSingleDesktopTableSettings.getColumns(
                      this.state.sortedColumn,
                      duration === "28d",
                      this.props.enableQualificationSideBar && this.props.handleOpenQualificationSideBar,
                  )
                : IncomingTrafficCompareDesktopTableSettings.getColumns(
                      this.state.sortedColumn,
                      this.props.enableQualificationSideBar && this.props.handleOpenQualificationSideBar,
                  );
        } else {
            return mode === "single"
                ? IncomingTrafficSingleTableSettings.getColumns(
                      this.state.sortedColumn,
                      duration === "28d",
                      this.props.enableQualificationSideBar && this.props.handleOpenQualificationSideBar,
                  )
                : IncomingTrafficCompareTableSettings.getColumns(
                      this.state.sortedColumn,
                      this.props.enableQualificationSideBar && this.props.handleOpenQualificationSideBar,
                  );
        }
    };

    public getInitialFilters = () => {
        let searchFilter = "";
        let categoryFilter = "";
        let sourceTypeFilter = "";
        const params = this.swNavigator.getParams();
        const isCompare = params.key.split(",").length > 1;

        params.filter = {};
        if (this.isStructuredSource(params.webSource)) {
            params.timeGranularity = params.duration === "28d" ? "Daily" : "Monthly";
            delete params.channelAnnalysisGranularity;
            delete params.iso;
            params.includeSubDomains = params.isWWW === "*";
            delete params.isWWW;
            params.keys = params.key;
            delete params.key;
            delete params.category;
            if (params.tableCategory === "no-category") {
                delete params.tableCategory;
            }
            if (params.tableCategory) {
                categoryFilter = params.tableCategory;
            }

            if (params.search) {
                searchFilter = params.search;
                delete params.search;
            }

            if (params.sourceType) {
                sourceTypeFilter = params.sourceType;
            }
            // orderBy
            if (!this.state.sortedColumn) {
                params.orderBy = "Share desc";
            } else {
                params.orderBy = `${this.state.sortedColumn.field} ${this.state.sortedColumn.sortDirection}`;
            }
            const { filter } = buildFiltersForTable({
                search: searchFilter,
                category: categoryFilter,
                sourceType: sourceTypeFilter,
            });
            params.filter = filter;
        } else {
            // category
            if (params.referralsCategory) {
                params.tableCategory = params.referralsCategory;
                delete params.referralsCategory;
            }
            delete params.category;
            if (params.tableCategory === "") {
                delete params.tableCategory;
            }

            if (params.tableCategory) {
                categoryFilter = params.tableCategory;
            }

            // search
            if (params.search) {
                searchFilter = params.search;
                delete params.search;
            }

            // orderBy
            if (!this.state.sortedColumn) {
                params.orderBy = "TotalShare desc";
            } else {
                params.orderBy = `${this.state.sortedColumn.field} ${this.state.sortedColumn.sortDirection}`;
            }
            const { filter } = buildFiltersForTable({
                search: searchFilter,
                category: categoryFilter,
            });
            params.filter = filter;
        }

        const apiParams = apiHelper.transformParamsForAPI(params);
        delete apiParams.iso;
        return apiParams;
    };

    public getApiEndpoint = () => {
        const { webSource } = this.swNavigator.getParams();
        return webSource === "Total"
            ? "/widgetApi/MarketingMixTotal/TrafficSourcesOverview/Table"
            : "/widgetApi/MarketingMix/TrafficSourcesOverviewData/Table";
    };

    public transformStructuredData = (data) => {
        return {
            records: data.Data.map((record) => {
                return {
                    ...record,
                    url: this.swNavigator.href("digitalsuite_website_websiteperformance", {
                        ...this.swNavigator.getParams(),
                        key: record.Domain,
                    }),
                };
            }),
            TotalCount: data.TotalCount,
            Filters: data.Filters,
        };
    };

    public transformData = (data) => {
        const params = this.swNavigator.getParams();
        const keys = params.key.split(",");
        return {
            ...data,
            records: data.Records.map((record) => {
                return {
                    ...record,
                    url: this.swNavigator.href("digitalsuite_website_websiteperformance", {
                        ...this.swNavigator.getParams(),
                        key: record.Domain,
                    }),
                    // country: this.props.country,
                    trafficDistribution: record.SiteOrigins
                        ? Object.entries(record.SiteOrigins).map(([site, value], index) => {
                              const colorIndex = keys.indexOf(site);
                              return {
                                  color: colorsPalettes.carbon[0],
                                  backgroundColor: CHART_COLORS.main[colorIndex],
                                  width: value,
                                  // @ts-expect-error FIXME: Argument of type 'unknown' is not assignable to parameter of type 'string'
                                  text: smallNumbersPercentageFilter()(value, 1),
                                  name: site,
                              };
                          })
                        : [],
                    Rank: record.Rank === -1 ? 0 : record.Rank,
                };
            }),
        };
    };

    public onGetStructuredData = (data: IDesktopBackendData) => {
        this.setState({
            allCategories: data?.Filters?.category,
            allSourceTypes: data?.Filters?.sourceType,
        });
    };

    public onGetData = (data: IBackendData) => {
        this.setState({
            allCategories: data.AllCategories,
            topCategories: data.TopCategories,
            totalSectionData: {
                totalShare: data.TotalShare > 0.99 ? 1 : data.TotalShare,
                totalUnGroupedCount: data.TotalUnGroupedCount,
                totalVisits: data.TotalVisits,
            },
            topics: data.Topics,
            categoriesData: similarweb.utils
                .formatTopList(data.TopCategories, 6, {
                    transformFunction: similarweb.utils.addCategoryIdFromName,
                })
                .map((cat) => {
                    return {
                        category: i18nCategoryFilter()(cat.Name),
                        categoryApi: cat.Id,
                        ...Object.entries(data.Categories).reduce((acc, [domain, domainCategories]) => {
                            return {
                                ...acc,
                                [domain]: cat.Value,
                            };
                        }, {}),
                    };
                }),
            domainMetaData: this.chosenSites.map((site) => {
                return {
                    icon: this.chosenSites.listInfo[site].icon,
                    name: site,
                    color: this.chosenSites.getSiteColor(site),
                };
            }),
        });
    };

    public getExcel = () => {
        const params = this.getInitialFilters();
        const queryStringParams = queryString.stringify(params);
        if (params.webSource === "Total") {
            return `/widgetApi/MarketingMixTotal/TrafficSourcesOverview/Data/Excel?${queryStringParams}`;
        }
        return `export/analysis/GetTrafficSourcesReferralsTsv?${queryStringParams}`;
    };

    public dataParamsAdapter = (params) => {
        const transformedParams = { ...params };
        // orderby
        if (transformedParams.sort) {
            transformedParams.orderBy = `${transformedParams.sort} ${transformedParams.asc ? "asc" : "desc"}`;
            delete transformedParams.sort;
            delete transformedParams.asc;
        }
        return transformedParams;
    };

    public onSort = ({ field, sortDirection }) => {
        this.setState({
            sortedColumn: {
                field,
                sortDirection,
            },
        });
    };
}
export const IncomingTrafficRefferalTable = QualificationSideBarHOC(IncomingTrafficRefferalTableInner);
